/* eslint-disable react/jsx-props-no-spreading */
import { Alert, Box, Button, Grid, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useCallback, useMemo, useState } from 'react';

import CheckboxField from 'components/CheckboxField';
import InputAutocompleteQuery from 'components/InputAutocompleteQuery';
import useForm from 'hooks/useForm';
import useDealsByModelQuery from 'modules/Deal/hooks/useDealsByModelQuery';
import { confirmMissingCompanyWithSwal } from 'utils/confirmMissingCompany';
import { COLOR_ERROR, COLOR_PRIMARY } from 'utils/constants/colors';

import {
  FIELD_COMPANY,
  FIELD_CONTACT,
  FIELD_HOURLY_CHARGE,
  FIELD_ID,
  FIELD_REASON,
  FIELD_SITE,
  FIELD_SYSTEM,
  FIELD_TAXABLE,
  FIELD_TITLE,
  FIELD_TRAVEL_FEE,
  FIELD_TYPE,
  FIELD_WARRANTY,
} from '../constants/fields';
import TypeCard from './TypeCard';

export const INITIAL_VALUES = {
  [FIELD_CONTACT]: null,
  [FIELD_SITE]: null,
  [FIELD_COMPANY]: null,
  [FIELD_SYSTEM]: null,
  [FIELD_HOURLY_CHARGE]: 0,
  [FIELD_TRAVEL_FEE]: 0,
  [FIELD_TITLE]: '',
  [FIELD_TYPE]: { name: 'Work Order', value: null },
  [FIELD_REASON]: '',
  [FIELD_TAXABLE]: false,
  [FIELD_WARRANTY]: false,
};

const ServiceCallForm = ({
  submitting = false,
  cancelLabel = 'Cancel',
  submitLabel = 'Submit',
  submitLoadingLabel = 'Submitting…',
  errorMessage = null,
  values: initialValues = INITIAL_VALUES,
  errors: initialErrors = {},
  contactFieldOptions = [],
  siteFieldOptions = [],
  companyFieldOptions = [],
  systemFieldOptions = [],
  onChange,
  onCancel,
  onSubmit,
}) => {
  const [isSubmitPending, setIsSubmitPending] = useState(false);

  // Hooks
  const handleSubmit = useCallback(
    async (values, formikHelpers) => {
      if (submitting || isSubmitPending) {
        return;
      }

      setIsSubmitPending(true);
      let forceWithoutCompany = false;

      try {
        if (!values?.[FIELD_ID]) {
          const siteId = values?.[FIELD_SITE];
          const companyId = values?.[FIELD_COMPANY];

          if (siteId && !companyId) {
            const {
              confirmed,
              forceWithoutCompany: shouldForceWithoutCompany,
            } = await confirmMissingCompanyWithSwal({
              siteId,
              hasCompany: Boolean(companyId),
              entityLabel: 'work order',
            });

            if (!confirmed) {
              formikHelpers?.setSubmitting?.(false);
              return;
            }

            forceWithoutCompany = shouldForceWithoutCompany;
          }
        }

        const nextValues = forceWithoutCompany
          ? { ...values, force_without_company: true }
          : values;

        await onSubmit(nextValues, formikHelpers);
      } finally {
        setIsSubmitPending(false);
      }
    },
    [isSubmitPending, onSubmit, submitting],
  );

  const form = useForm({
    initialValues,
    initialErrors,
    onChange,
    onSubmit: handleSubmit,
  });

  const selectedContactId = form.values?.[FIELD_CONTACT];

  useDealsByModelQuery({
    model: 'contact',
    modelId: selectedContactId,
    cacheTime: 0,
    staleTime: 0,
    onSuccess: (_deals) => {
      const deal = _deals.at(0);

      if (deal) {
        form.setValues((currentValues) => {
          if (
            currentValues[FIELD_CONTACT] !== selectedContactId ||
            currentValues[FIELD_SITE] ||
            currentValues[FIELD_COMPANY] ||
            currentValues[FIELD_SYSTEM]
          ) {
            return currentValues;
          }

          return {
            ...currentValues,
            [FIELD_SITE]: deal.site_id,
            [FIELD_COMPANY]: deal.company_id,
            [FIELD_SYSTEM]: deal.systems?.at(0)?.id ?? null,
          };
        });
      }
    },
  });

  // Handlers

  const handleCancelClick = useCallback(() => onCancel(), [onCancel]);

  const handleSelectType = useCallback(
    (selectedType) => form.setFieldValue(FIELD_TYPE, selectedType),
    [form],
  );

  const handleContactChange = useCallback(
    (contactId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_CONTACT] === contactId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_CONTACT]: contactId,
        };
      });
    },
    [form],
  );

  const handleSiteChange = useCallback(
    (siteId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_SITE] === siteId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_SITE]: siteId,
        };
      });
    },
    [form],
  );

  const handleCompanyChange = useCallback(
    (companyId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_COMPANY] === companyId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_COMPANY]: companyId,
        };
      });
    },
    [form],
  );

  // Computable

  const siteFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
    }),
    [form.values],
  );

  const companyFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
    }),
    [form.values],
  );

  const systemFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
      company_id: form.values[FIELD_COMPANY],
      site_id: form.values[FIELD_SITE],
    }),
    [form.values],
  );

  const submitDisabled = useMemo(() => {
    if (submitting || isSubmitPending) {
      return true;
    }

    return (
      !form.values[FIELD_CONTACT] ||
      !form.values[FIELD_SITE] ||
      !form.values[FIELD_TITLE] ||
      !form.values[FIELD_REASON]
    );
  }, [form.values, isSubmitPending, submitting]);

  const formDisabled = useMemo(
    () => submitting || isSubmitPending,
    [isSubmitPending, submitting],
  );

  // Render

  return (
    <Box component="form" onSubmit={form.handleSubmit}>
      <Grid container={true} spacing={3}>
        {errorMessage && (
          <Grid item={true} md={12} xs={12}>
            <Alert severity={COLOR_ERROR}>{errorMessage}</Alert>
          </Grid>
        )}
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={formDisabled}
            queryKeyPrefix="service_call_contact"
            label="Contact"
            required={true}
            placeholder="Search Contacts…"
            options={contactFieldOptions}
            value={form.values[FIELD_CONTACT] ?? null}
            onChange={handleContactChange}
            {...form.getMuiFieldErrorProps(FIELD_CONTACT)}
            data-testid="contact_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={formDisabled}
            queryKeyPrefix="service_call_site"
            label="Site"
            required={true}
            placeholder="Search Sites…"
            options={siteFieldOptions}
            optionsParams={siteFieldOptionsParams}
            value={form.values[FIELD_SITE] ?? null}
            onChange={handleSiteChange}
            {...form.getMuiFieldErrorProps(FIELD_SITE)}
            data-testid="site_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={formDisabled}
            queryKeyPrefix="service_call_company"
            label="Company"
            placeholder="Search Companies…"
            options={companyFieldOptions}
            optionsParams={companyFieldOptionsParams}
            value={form.values[FIELD_COMPANY] ?? null}
            onChange={handleCompanyChange}
            {...form.getMuiFieldErrorProps(FIELD_COMPANY)}
            data-testid="company_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={formDisabled}
            queryKeyPrefix="service_call_system"
            label="System"
            placeholder="Search Systems…"
            options={systemFieldOptions}
            optionsParams={systemFieldOptionsParams}
            value={form.values[FIELD_SYSTEM] ?? null}
            onChange={form.getFieldValueChangeHandler(FIELD_SYSTEM)}
            {...form.getMuiFieldErrorProps(FIELD_SYSTEM)}
            data-testid="system_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <TextField
            disabled={formDisabled}
            fullWidth={true}
            label="Hourly Charge"
            type="number"
            variant="outlined"
            inputProps={{
              'data-testid': 'hourly_charge_field',
            }}
            {...form.getFieldProps(FIELD_HOURLY_CHARGE)}
            {...form.getMuiFieldErrorProps(FIELD_HOURLY_CHARGE)}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <TextField
            disabled={formDisabled}
            fullWidth={true}
            label="Travel Fee"
            type="number"
            variant="outlined"
            inputProps={{
              'data-testid': 'total_fee_field',
            }}
            {...form.getFieldProps(FIELD_TRAVEL_FEE)}
            {...form.getMuiFieldErrorProps(FIELD_TRAVEL_FEE)}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <TextField
            disabled={formDisabled}
            fullWidth={true}
            required={true}
            label="Title"
            variant="outlined"
            inputProps={{
              'data-testid': 'title_field',
            }}
            {...form.getFieldProps(FIELD_TITLE)}
            {...form.getMuiFieldErrorProps(FIELD_TITLE)}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <TypeCard
            selectType={handleSelectType}
            type={form.values[FIELD_TYPE]}
            showJustAutocomplete={true}
          />
        </Grid>
        <Grid item={true} md={12} xs={12}>
          <TextField
            disabled={formDisabled}
            fullWidth={true}
            multiline={true}
            required={true}
            rows={4}
            label="Reason"
            variant="outlined"
            inputProps={{
              'data-testid': 'reason_field',
            }}
            {...form.getFieldProps(FIELD_REASON)}
            {...form.getMuiFieldErrorProps(FIELD_REASON)}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <CheckboxField
            disabled={formDisabled}
            label="Taxable"
            value={form.values[FIELD_TAXABLE] ?? false}
            onChange={form.getFieldValueChangeHandler(FIELD_TAXABLE)}
            {...form.getMuiFieldErrorProps(FIELD_TAXABLE)}
            data-testid="taxable_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <CheckboxField
            disabled={formDisabled}
            label="Warranty"
            value={form.values[FIELD_WARRANTY] ?? false}
            onChange={form.getFieldValueChangeHandler(FIELD_WARRANTY)}
            {...form.getMuiFieldErrorProps(FIELD_WARRANTY)}
            data-testid="warranty_field"
          />
        </Grid>
        <Grid
          item={true}
          md={12}
          xs={12}
          display="flex"
          gap={2}
          justifyContent="space-between"
        >
          <Button
            disabled={formDisabled}
            type="button"
            variant="outlined"
            color={COLOR_PRIMARY}
            onClick={handleCancelClick}
            data-testid="cancel_button"
          >
            {cancelLabel}
          </Button>
          <Button
            disabled={submitDisabled}
            type="submit"
            variant="contained"
            color={COLOR_PRIMARY}
            data-testid="submit_button"
          >
            {submitting ? submitLoadingLabel : submitLabel}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

ServiceCallForm.propTypes = {
  submitting: PropTypes.bool,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  submitLoadingLabel: PropTypes.string,
  errorMessage: PropTypes.string,
  values: PropTypes.shape({
    [FIELD_CONTACT]: PropTypes.number,
    [FIELD_SITE]: PropTypes.number,
    [FIELD_COMPANY]: PropTypes.number,
    [FIELD_SYSTEM]: PropTypes.number,
    [FIELD_HOURLY_CHARGE]: PropTypes.number,
    [FIELD_TRAVEL_FEE]: PropTypes.number,
    [FIELD_TITLE]: PropTypes.string,
    [FIELD_TYPE]: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    [FIELD_REASON]: PropTypes.string,
    [FIELD_TAXABLE]: PropTypes.bool,
    [FIELD_WARRANTY]: PropTypes.bool,
  }),
  errors: PropTypes.shape({
    [FIELD_CONTACT]: PropTypes.string,
    [FIELD_SITE]: PropTypes.string,
    [FIELD_COMPANY]: PropTypes.string,
    [FIELD_SYSTEM]: PropTypes.string,
    [FIELD_HOURLY_CHARGE]: PropTypes.string,
    [FIELD_TRAVEL_FEE]: PropTypes.string,
    [FIELD_TITLE]: PropTypes.string,
    [FIELD_REASON]: PropTypes.string,
    [FIELD_TAXABLE]: PropTypes.string,
    [FIELD_WARRANTY]: PropTypes.string,
  }),
  contactFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  siteFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  companyFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  systemFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  onChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * @type {ServiceCallForm}
 */
export default memo(ServiceCallForm);
